import { FetchCopcWorker } from '../../../workers/worker-imports';
import { CopcWorkerRequest, CopcWorkerResponse } from './types';

/** A worker fetches a node while it decodes another. */
const REQUESTS_PER_WORKER = 2;

type PendingRequest = {
  resolve: (result: unknown) => void;
  reject: (err: Error) => void;
  worker: PooledWorker;
};

type PooledWorker = { worker: Worker; active: number };

/** Leaves a core for the main thread, and caps the workers so they don't crowd out the rest. */
function defaultWorkerCount() {
  const cores = navigator.hardwareConcurrency || 4;
  return Math.max(1, Math.min(4, cores - 1));
}

/**
 * Workers that read a COPC with range requests and decompress its points, so neither blocks the
 * main thread. A request goes to the worker with the fewest requests, and its point arrays are
 * transferred back, not copied.
 */
export class CopcWorkerPool {
  private workers: PooledWorker[];
  private pending = new Map<number, PendingRequest>();
  private nextId = 0;

  constructor(workerCount = defaultWorkerCount()) {
    this.workers = Array.from({ length: workerCount }, () => {
      const pooled: PooledWorker = { worker: new FetchCopcWorker(), active: 0 };
      pooled.worker.onmessage = (event: MessageEvent<CopcWorkerResponse>) =>
        this.settle(event.data);
      pooled.worker.onerror = (event: ErrorEvent) => this.fail(pooled, event);
      return pooled;
    });
  }

  /** How many requests the workers take at once before they queue up. */
  get capacity() {
    return this.workers.length * REQUESTS_PER_WORKER;
  }

  get activeRequests() {
    return this.pending.size;
  }

  request<T>(request: DistributiveOmit<CopcWorkerRequest, 'id'>): Promise<T> {
    if (this.workers.length === 0) {
      return Promise.reject(new Error('No COPC worker is running'));
    }
    const worker = this.workers.reduce((least, pooled) =>
      pooled.active < least.active ? pooled : least,
    );
    const id = this.nextId++;

    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (result: unknown) => void, reject, worker });
      worker.active++;
      worker.worker.postMessage({ ...request, id });
    });
  }

  dispose() {
    for (const { worker } of this.workers) {
      worker.terminate();
    }
    // Later requests reject, rather than wait for workers that are gone
    this.workers = [];
    for (const pending of this.pending.values()) {
      pending.reject(new Error('The COPC workers were stopped'));
    }
    this.pending.clear();
  }

  /**
   * Takes a worker that failed out of the pool, e.g. one whose script didn't load, which never
   * answers its requests.
   */
  private fail(pooled: PooledWorker, event: ErrorEvent) {
    pooled.worker.terminate();
    this.workers = this.workers.filter((worker) => worker !== pooled);

    const error = new Error(event.message || 'A COPC worker failed');
    for (const [id, pending] of this.pending) {
      if (pending.worker === pooled) {
        this.pending.delete(id);
        pending.reject(error);
      }
    }
  }

  private settle(response: CopcWorkerResponse) {
    const pending = this.pending.get(response.id);
    if (!pending) {
      return;
    }

    this.pending.delete(response.id);
    pending.worker.active--;
    if ('error' in response) {
      pending.reject(new Error(response.error));
    } else {
      pending.resolve(response.result);
    }
  }
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
